/**
 * Asset Type Configuration Schemas
 *
 * Defines factory-configurable asset types with colors, icons,
 * and type-specific custom properties. Replaces the hardcoded enum approach.
 *
 * Each factory can define its own asset types. When no config exists,
 * DEFAULT_ASSET_TYPES provides backward-compatible defaults matching
 * the original hardcoded enum.
 */

import { z } from 'zod';

// Property types for custom type-specific fields
export const propertyTypeSchema = z.enum(['text', 'number', 'select', 'boolean']);

// Schema for a single custom property definition on an asset type
export const assetTypePropertySchema = z.object({
  key: z.string().min(1).max(50),
  label: z.string().min(1).max(100),
  type: propertyTypeSchema,
  required: z.boolean().default(false),
  options: z.array(z.string()).optional(),
  placeholder: z.string().optional(),
  unit: z.string().max(20).optional(),
});

// Schema for a single asset type configuration
export const assetTypeConfigSchema = z.object({
  id: z.string().min(1).max(50),
  label: z.string().min(1).max(100),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'factory.assetTypes.validation.color_format'),
  icon: z.string().min(1).max(50),
  properties: z.array(assetTypePropertySchema).optional(),
});

// Schema for the full factory asset types config array
export const assetTypesConfigSchema = z.array(assetTypeConfigSchema).min(1).max(50);

// Inferred types
export type PropertyType = z.infer<typeof propertyTypeSchema>;
export type AssetTypeProperty = z.infer<typeof assetTypePropertySchema>;
export type AssetTypeConfig = z.infer<typeof assetTypeConfigSchema>;

/**
 * Default asset types matching the original hardcoded enum.
 * Used as fallback when factory has no assetTypes config.
 */
export const DEFAULT_ASSET_TYPES: AssetTypeConfig[] = [
  { id: 'vehicle', label: 'Vehicle', color: '#3B82F6', icon: 'truck' },
  { id: 'equipment', label: 'Equipment', color: '#A855F7', icon: 'wrench' },
  { id: 'person', label: 'Person', color: '#22C55E', icon: 'user' },
  { id: 'material', label: 'Material', color: '#EAB308', icon: 'package' },
  { id: 'tool', label: 'Tool', color: '#F97316', icon: 'hammer' },
  { id: 'forklift', label: 'Forklift', color: '#3B82F6', icon: 'forklift' },
  { id: 'tugger_train', label: 'Tugger Train', color: '#F97316', icon: 'train-front' },
  { id: 'other', label: 'Other', color: '#6B7280', icon: 'circle' },
];

/**
 * Get an asset type config by its ID from a config array.
 * Falls back to a generated config with neutral color if not found.
 */
export function getAssetTypeById(typeId: string, config: AssetTypeConfig[]): AssetTypeConfig {
  return (
    config.find(t => t.id === typeId) ?? {
      id: typeId,
      label: typeId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      color: '#6B7280',
      icon: 'circle',
    }
  );
}

/**
 * Build a label map from asset type config (for dropdowns, display).
 */
export function buildAssetTypeLabels(config: AssetTypeConfig[]): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const type of config) {
    labels[type.id] = type.label;
  }
  return labels;
}
